import { create } from 'zustand';
import {
  getUserNotifications,
  getUnreadCount,
  markAsRead,
  markAllAsRead,
  deleteNotification,
  Notification
} from '@/services/Common/NotificationService';

interface NotificationStore {
  notifications: Notification[];
  unreadCount: number;
  loading: boolean;
  error: string | null;

  // Methods
  fetchNotifications: (limit?: number, offset?: number, type?: string) => Promise<void>;
  fetchUnreadCount: () => Promise<void>;
  markNotificationAsRead: (uuid: string) => Promise<void>;
  markAllNotificationsAsRead: () => Promise<void>;
  deleteNotification: (uuid: string) => Promise<void>;
  resetStore: () => void;
  refreshAll: () => Promise<void>;
}

const useNotificationStore = create<NotificationStore>((set, get) => ({
  notifications: [],
  unreadCount: 0,
  loading: false,
  error: null,

  fetchNotifications: async (limit = 20, offset = 0, type?: string) => {
    try {
      set({ loading: true, error: null });
      const response = await getUserNotifications(limit, offset, type);

      if (response.success) {
        set({ notifications: response.data, loading: false });
      } else {
        set({ error: response.error?.message || 'Failed to fetch notifications', loading: false });
      }
    } catch (error: any) {
      set({ error: error.message, loading: false });
    }
  },

  fetchUnreadCount: async () => {
    try {
      const response = await getUnreadCount();

      if (response.success) {
        set({ unreadCount: response.data.unread_count });
      } else {
        console.error('Failed to fetch unread count:', response.error?.message);
      }
    } catch (error: any) {
      console.error('Error fetching unread count:', error);
    }
  },

  markNotificationAsRead: async (uuid: string) => {
    try {
      const response = await markAsRead(uuid);

      if (response.success) {
        // Update local state
        set((state) => ({
          notifications: state.notifications.map((n) =>
            n.uuid === uuid ? { ...n, is_read: true } : n
          ),
          unreadCount: Math.max(0, state.unreadCount - 1)
        }));
      } else {
        console.error('Failed to mark notification as read:', response.error?.message);
      }
    } catch (error: any) {
      console.error('Error marking notification as read:', error);
    }
  },

  markAllNotificationsAsRead: async () => {
    try {
      const response = await markAllAsRead();

      if (response.success) {
        set((state) => ({
          notifications: state.notifications.map((n) => ({ ...n, is_read: true })),
          unreadCount: 0
        }));
      } else {
        console.error('Failed to mark all as read:', response.error?.message);
      }
    } catch (error: any) {
      console.error('Error marking all as read:', error);
    }
  },

  deleteNotification: async (uuid: string) => {
    try {
      const response = await deleteNotification(uuid);

      if (response.success) {
        set((state) => {
          const notification = state.notifications.find((n) => n.uuid === uuid);
          return {
            notifications: state.notifications.filter((n) => n.uuid !== uuid),
            unreadCount:
              notification && !notification.is_read
                ? Math.max(0, state.unreadCount - 1)
                : state.unreadCount
          };
        });
      } else {
        console.error('Failed to delete notification:', response.error?.message);
      }
    } catch (error: any) {
      console.error('Error deleting notification:', error);
    }
  },

  resetStore: () => {
    set({ notifications: [], unreadCount: 0, loading: false, error: null });
  },

  refreshAll: async () => {
    const { fetchNotifications, fetchUnreadCount } = get();
    await Promise.all([fetchNotifications(), fetchUnreadCount()]);
  }
}));

export default useNotificationStore;
